/**
 * policyEnvelope — the TS shape of every account-default / location-value
 * settings endpoint, and the small helpers that turn one into UI state.
 *
 * Contract (backend, /api/accounts/{accountId}/…): every GET returns the FLAT
 * effective fields at the top level plus
 *   sources:   { <field>: 'system' | 'account' | 'location' }
 *   overrides: sparse location row | null   (location GETs only)
 *   account:   the account row | null       (so a location screen can print
 *                                            "Account setting: X")
 * PATCH bodies are tri-state: absent key = untouched, null = clear the cell
 * (follow the account / the system default), value = set.
 *
 * Locks: every envelope also carries `locked` — the field keys the account
 * has locked against location overrides. A locked field resolves from the
 * account regardless of the location cell (the cell is kept; unlocking
 * restores it). Account rows carry the same list as `locked_fields`, and
 * account writes may send `locked_fields` to replace it ([] unlocks all). A
 * location write on a locked field is refused with 409 POLICY_FIELD_LOCKED,
 * so location drafts never send one (see buildPatchBody). The override PIN
 * is never lockable.
 *
 * Vocabulary used in copy: a location field is "Following account",
 * "Customized" or "Set by account" (locked); the account side shows
 * "System default" or its own value.
 */

export type PolicySource = 'system' | 'account' | 'location';

/** One source per field key. */
export type PolicySources<K extends string = string> = Partial<Record<K, PolicySource>>;

/**
 * The generic envelope: `TFields` is the flat effective field set, `TRow` the
 * sparse row shape (defaults to a partial of the fields).
 */
export type PolicyEnvelope<TFields extends object, TRow extends object = Partial<TFields>> = TFields & {
  sources: PolicySources<Extract<keyof TFields, string>>;
  /** Field keys the account has locked for every location (never null from the server). */
  locked: string[];
  overrides?: TRow | null;
  account?: (TRow & { locked_fields?: string[] }) | null;
};

/** Tri-state PATCH body: absent = untouched, null = clear, value = set. */
export type PolicyPatch<TFields extends object> = { [K in keyof TFields]?: TFields[K] | null };

/** An account-scope write: the tri-state fields plus, when it changed, the lock list. */
export type AccountPolicyPatch<TFields extends object> = PolicyPatch<TFields> & { locked_fields?: string[] };

/** The lock list of an envelope, tolerant of an older server that omits it. */
export function lockedFieldsOf(envelope: { locked?: string[] | null } | null | undefined): string[] {
  return Array.isArray(envelope?.locked) ? envelope!.locked! : [];
}

export function isFieldLocked(envelope: { locked?: string[] | null } | null | undefined, field: string): boolean {
  return lockedFieldsOf(envelope).includes(field);
}

/** Same set of keys, any order. */
export function sameLockSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((k) => s.has(k));
}

/** The new lock list when it differs from the baseline, else undefined (absent from the body). */
export function diffLockedFields(before: readonly string[], after: readonly string[]): string[] | undefined {
  return sameLockSet(before, after) ? undefined : [...after];
}

/** Add or remove one key in a lock list (order preserved, no duplicates). */
export function toggleLockedField(list: readonly string[], field: string, locked: boolean): string[] {
  if (locked) return list.includes(field) ? [...list] : [...list, field];
  return list.filter((k) => k !== field);
}

/**
 * Re-apply one card's lock edits onto the list the server holds NOW. Two
 * cards can edit the same account row (Geofence and Fraud / Auto logout both
 * write work_session_policies), each from its own copy of the list; sending a
 * stale copy would silently unlock what the other card just locked. Only the
 * keys in `fields` are compared, so a card never touches another card's locks.
 */
export function mergeLockedFields(
  current: readonly string[],
  baseline: readonly string[],
  draft: readonly string[],
  fields: readonly string[]
): string[] {
  let out = [...current];
  for (const f of fields) {
    const now = draft.includes(f);
    if (baseline.includes(f) === now) continue;
    out = toggleLockedField(out, f, now);
  }
  return out;
}

/**
 * "22:00" → how the viewer's browser renders an <input type="time"> holding
 * it: "10:00 PM" in a 12-hour locale, "22:00" in a 24-hour one. Every helper
 * line that prints a time of day ("Account setting: …", "Locks at …") goes
 * through here so it reads like the control beside it. Anything that is not
 * HH:MM comes back unchanged; empty is "—".
 */
export function formatTimeOfDay(value: string | null | undefined): string {
  const m = /^(\d{2}):(\d{2})/.exec(value ?? '');
  if (!m) return value ? String(value) : '—';
  return new Date(2000, 0, 1, Number(m[1]), Number(m[2])).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * Source keys that are per-location by design rather than a departure from
 * the account: the override PIN (the account's is only a fallback). Never a
 * "custom" cell, never in the deviations view.
 */
export const PER_LOCATION_SOURCE_KEYS: readonly string[] = ['override_pin'];

/** Number of fields the location has customized (per-location keys excluded). */
export function countCustom(sources: PolicySources | null | undefined): number {
  if (!sources) return 0;
  return Object.entries(sources).filter(([k, s]) => s === 'location' && !PER_LOCATION_SOURCE_KEYS.includes(k)).length;
}

// ── Work-order checks (VIN duplicate / service duplicate / override PIN) ────

export interface WorkOrderCheckFields {
  vin_duplicate_block: boolean;
  vin_duplicate_cooldown_days: number;
  service_duplicate_enabled: boolean;
  service_duplicate_cooldown_days: number;
}

export type WorkOrderCheckField = keyof WorkOrderCheckFields;

/** The PIN is write-only; rows carry only whether one is set. */
export interface WorkOrderCheckRow extends Partial<WorkOrderCheckFields> {
  override_pin_set?: boolean;
}

export type WorkOrderCheckSourceKey = WorkOrderCheckField | 'override_pin';

export interface WorkOrderCheckPolicyEnvelope extends WorkOrderCheckFields {
  override_pin_set: boolean;
  sources: PolicySources<WorkOrderCheckSourceKey>;
  /** Check fields the account has locked (the PIN is never lockable). */
  locked: string[];
  overrides?: WorkOrderCheckRow | null;
  account?: (WorkOrderCheckRow & { locked_fields?: string[] }) | null;
}

export type WorkOrderCheckPatch = PolicyPatch<WorkOrderCheckFields>;
/** Account PATCH: the fields plus `locked_fields` when the lock list changed. */
export type WorkOrderCheckAccountPatch = AccountPolicyPatch<WorkOrderCheckFields>;

export const WORK_ORDER_CHECK_SYSTEM_DEFAULTS: WorkOrderCheckFields = {
  vin_duplicate_block: false,
  vin_duplicate_cooldown_days: 0,
  service_duplicate_enabled: false,
  service_duplicate_cooldown_days: 30,
};

export const WORK_ORDER_CHECK_FIELDS: WorkOrderCheckField[] = [
  'vin_duplicate_block',
  'vin_duplicate_cooldown_days',
  'service_duplicate_enabled',
  'service_duplicate_cooldown_days',
];

// ── Work-order edit lock ────────────────────────────────────────────────────

export interface EditLockFields {
  mode: import('@turn-connect/shared').EditLockMode;
  days: number | null;
  grace_days: number;
  week_start: import('@turn-connect/shared').EditLockWeekStart;
  lock_time: string;
}

export type EditLockField = keyof EditLockFields;

export type EditLockPolicyEnvelope = PolicyEnvelope<EditLockFields, Partial<EditLockFields>> & {
  id?: string;
  account_id?: string;
  location_id?: string | null;
};

export type EditLockPatch = PolicyPatch<EditLockFields>;

export const EDIT_LOCK_FIELDS: EditLockField[] = ['mode', 'days', 'grace_days', 'week_start', 'lock_time'];

/** The edit-lock cells that hold a time of day (strict HH:MM on the wire — the column is CHECK-constrained to it). */
export const EDIT_LOCK_TIME_FIELDS: readonly EditLockField[] = ['lock_time'];

export const EDIT_LOCK_FAMILY = 'edit_lock';

// ── Location defaults (labor rate / auto-approval amount) ───────────────────

/**
 * Money cells travel as decimal strings ("80", "300.00"); null = unset at
 * every scope. 0 or null auto-approval = approvals always required.
 */
export interface LocationDefaultsFields {
  labor_rate: string | null;
  auto_approval_amount: string | null;
}

export type LocationDefaultsField = keyof LocationDefaultsFields;

export const LOCATION_DEFAULTS_FIELDS: LocationDefaultsField[] = ['labor_rate', 'auto_approval_amount'];

export const LOCATION_DEFAULTS_FAMILY = 'location_defaults';

export const LOCATION_DEFAULTS_LABELS: Record<LocationDefaultsField, string> = {
  labor_rate: 'Labor rate',
  auto_approval_amount: 'Auto-approval amount',
};

export type LocationDefaultsEnvelope = PolicyEnvelope<LocationDefaultsFields> & {
  account_id: string;
  location_id: string | null;
};

/** PATCH body: numbers or decimal strings; null clears the cell. */
export type LocationDefaultsPatch = { [K in LocationDefaultsField]?: string | number | null };
export type LocationDefaultsAccountPatch = LocationDefaultsPatch & { locked_fields?: string[] };

/** "$80.00" / "—" for a decimal-string money cell. */
export function formatMoneyCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `$${n.toFixed(2)}`;
}

/** Input text → wire value: '' = null (unset), else the trimmed decimal string. */
export function moneyInputToCell(raw: string): string | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? String(t) : null;
}

// ── Draft → PATCH ───────────────────────────────────────────────────────────

/** Per-field draft state on a location screen. */
export interface FieldDraft<V> {
  /** Current control value (the account value while following). */
  value: V;
  /** True = "Customized"; false = "Following account". */
  custom: boolean;
}

export type Draft<TFields extends object> = { [K in keyof TFields]: FieldDraft<TFields[K]> };

/**
 * Build the draft from an envelope: value = effective, custom = source is
 * location. A locked field is never custom — the account resolves it
 * whatever the location cell holds.
 */
export function draftFromEnvelope<TFields extends object>(
  envelope: TFields & { sources: PolicySources; locked?: string[] | null },
  fields: Array<keyof TFields & string>
): Draft<TFields> {
  const draft = {} as Draft<TFields>;
  const locked = lockedFieldsOf(envelope);
  for (const f of fields) {
    draft[f] = { value: envelope[f], custom: !locked.includes(f) && envelope.sources[f] === 'location' };
  }
  return draft;
}

/**
 * Diff a draft against its baseline into a tri-state PATCH body:
 *   custom and changed (or newly custom)  → value
 *   was custom, now following             → null
 *   untouched                             → absent
 *   locked by the account                 → never sent (409 otherwise)
 * Returns null when nothing changed.
 */
export function buildPatchBody<TFields extends object>(
  baseline: Draft<TFields>,
  draft: Draft<TFields>,
  fields: Array<keyof TFields & string>,
  locked: readonly string[] = []
): PolicyPatch<TFields> | null {
  const body: PolicyPatch<TFields> = {};
  let touched = false;
  for (const f of fields) {
    if (locked.includes(f)) continue;
    const before = baseline[f];
    const after = draft[f];
    if (after.custom) {
      if (!before.custom || before.value !== after.value) {
        body[f] = after.value;
        touched = true;
      }
    } else if (before.custom) {
      body[f] = null;
      touched = true;
    }
  }
  return touched ? body : null;
}

/** Diff two flat field sets (account side): only changed keys, values as-is. */
export function diffFields<TFields extends object>(
  before: TFields,
  after: TFields,
  fields: Array<keyof TFields & string>
): PolicyPatch<TFields> | null {
  const body: PolicyPatch<TFields> = {};
  let touched = false;
  for (const f of fields) {
    if (before[f] !== after[f]) {
      body[f] = after[f];
      touched = true;
    }
  }
  return touched ? body : null;
}
